/**
 * Slope plot rendered directly underneath the original-data plot, with a
 * show/hide toggle. Markus's requirement: "Der Plot der Steigung soll direkt
 * unter dem Plot der Originaldaten sein, und ein- und ausklappbar sein."
 *
 * State persists across the session via sessionStorage so a refresh keeps the
 * user's choice.
 */
import React, { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotData } from '../types';
import AnomalyChart from './AnomalyChart';

const STORAGE_KEY = 'anomaly.slopeExpanded';

interface Props {
  slope: PlotData;
  /** Line color (hex); passed through to the inner AnomalyChart. */
  color?: string;
  /** Shared zoom range with the sibling original-data chart. */
  sharedBrushRange?: { startTime: number; endTime: number } | null;
  onSharedBrushChange?: (range: { startTime: number; endTime: number } | null) => void;
}

const SlopeCollapsiblePlotImpl: React.FC<Props> = ({
  slope,
  color,
  sharedBrushRange,
  onSharedBrushChange,
}) => {
  const { t } = useTranslation('dataProcessing');
  const panelId = useId();
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(expanded));
    } catch {
      // Private mode / quota exceeded — silently degrade.
    }
  }, [expanded]);

  const toggle = useCallback(() => setExpanded((p) => !p), []);

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="text-sm text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring rounded px-2 py-1"
      >
        {expanded ? t('anomaly.actions.hideSlope') : t('anomaly.actions.showSlope')}
      </button>
      {expanded && (
        <div id={panelId} className="mt-2">
          <AnomalyChart
            plot={slope}
            color={color}
            hidePointsToggle
            sharedBrushRange={sharedBrushRange}
            onSharedBrushChange={onSharedBrushChange}
          />
        </div>
      )}
    </div>
  );
};

const SlopeCollapsiblePlot = React.memo(SlopeCollapsiblePlotImpl);
export default SlopeCollapsiblePlot;
